import { Eye, EyeOff } from "lucide-react"
import { type KeyboardEvent, useState } from "react"

/**
 * Un campo de la pantalla de acceso: rótulo, entrada de cristal y, al enfocarlo,
 * un hilo dorado que se traza bajo él desde el centro.
 *
 * ⚠ **El hilo AÑADE al foco, no lo sustituye.** El foco del sistema sigue siendo
 * `.campo-cristal:focus-visible` —la superficie sube y aparece el anillo—, que es
 * el vocabulario común del cristal. El hilo sólo existe aquí, en la única pantalla
 * que se presenta, y es transform + opacity: `scaleX` desde el centro.
 *
 * La contraseña trae dos ayudas que piden las veinte entradas diarias de cada quien:
 * **ver lo escrito** —un `<button>` de 44 px, con `aria-pressed`— y el **aviso de
 * Bloq Mayús**, que es la causa más común de «la contraseña no me entra».
 */
export function CampoAcceso({
  id,
  rotulo,
  tipo,
  autocompletar,
  valor,
  onCambio,
  invalido,
}: {
  id: string
  rotulo: string
  tipo: "email" | "password"
  autocompletar: string
  valor: string
  onCambio: (valor: string) => void
  invalido: boolean
}) {
  const esClave = tipo === "password"
  const [visible, setVisible] = useState(false)
  const [mayusculas, setMayusculas] = useState(false)

  // `getModifierState` existe en los eventos de teclado; con un autocompletado no
  // hay tecla y el aviso se queda como estaba, que es lo correcto.
  const revisarMayusculas = (e: KeyboardEvent<HTMLInputElement>) => {
    if (esClave) setMayusculas(e.getModifierState?.("CapsLock") ?? false)
  }

  return (
    <div className="group">
      {/* Etiqueta visible y asociada, nunca sólo `placeholder` (§15). Se enciende
          con el campo enfocado: dice dónde se está escribiendo. */}
      <label
        htmlFor={id}
        className="mb-1.5 block text-[0.6875rem] font-bold tracking-[0.05em] text-[rgb(var(--marco-tinta)/0.7)] uppercase transition-colors duration-[var(--duracion-entrada)] group-focus-within:text-[rgb(var(--marco-tinta)/1)]"
      >
        {rotulo}
      </label>
      <div className="relative">
        {/* Sin bloqueo de pegado: romper los gestores de contraseñas empeora la
            seguridad real, y WCAG 3.3.8 lo prohíbe explícitamente. */}
        <input
          id={id}
          name={id}
          type={esClave && !visible ? "password" : esClave ? "text" : tipo}
          autoComplete={autocompletar}
          required
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          onKeyDown={revisarMayusculas}
          onKeyUp={revisarMayusculas}
          onBlur={() => setMayusculas(false)}
          aria-invalid={invalido ? true : undefined}
          aria-describedby={esClave && mayusculas ? `${id}-mayus` : undefined}
          className={`campo-cristal ${esClave ? "pr-12" : ""}`}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-2.5 bottom-0 h-0.5 origin-center scale-x-0 rounded-full bg-[linear-gradient(90deg,rgb(var(--acceso-oro)/0),rgb(var(--acceso-oro-claro)),rgb(var(--acceso-oro)/0))] opacity-0 transition-[transform,opacity] duration-[220ms] ease-[var(--curva-entrada)] group-focus-within:scale-x-100 group-focus-within:opacity-100 motion-reduce:transition-none"
        />
        {esClave && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-pressed={visible}
            aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
            className="toque-cristal foco-cristal absolute inset-y-0 right-0.5 my-auto grid size-11 place-items-center rounded-[var(--radius-control)] text-[rgb(var(--marco-tinta)/0.7)] hover:text-[rgb(var(--marco-tinta)/1)]"
          >
            {visible ? (
              <EyeOff size={18} aria-hidden="true" />
            ) : (
              <Eye size={18} aria-hidden="true" />
            )}
          </button>
        )}
      </div>
      {/* Sin hueco reservado: la tarjeta tiene que caber en un teléfono tumbado
          (375 px de alto), y el aviso sólo existe mientras hace falta. */}
      {esClave && mayusculas && (
        <p
          id={`${id}-mayus`}
          role="status"
          className="mt-1.5 text-xs text-[rgb(var(--acceso-oro-claro))]"
        >
          Bloq Mayús está activado
        </p>
      )}
    </div>
  )
}
